import {KeyboardEvent, useRef} from 'react'
import {Trans, withNamespaces, WithNamespaces} from 'react-i18next'
import messages from '../../../assets/messages'
import {PammNetworkViewId} from '../types'
import {FullLabel, Segment, Segmented, ShortLabel} from './NetworkViewToggle.styled'

/** The DOM id of a view's tab, used by the tree panel's `aria-labelledby`. */
export const viewTabId = (view: PammNetworkViewId): string => `pamm-view-tab-${view}`

/** The DOM id of the tree panel the toggle controls, shared by every tab's `aria-controls`. */
export const NETWORK_PANEL_ID = 'pamm-network-panel'

/** Shape of an entry in `assets/messages`, which is plain JS and so carries no types of its own. */
type Message = {i18nKey: string; defaults: string}

/** Long and short label per view; the short one takes over below the `sm` breakpoint. */
const VIEW_LABELS: Record<PammNetworkViewId, {full: Message; short: Message}> = {
  master: {full: messages.pammViewMasterLabel, short: messages.pammViewMasterLabelShort},
  agent: {full: messages.pammViewAgentLabel, short: messages.pammViewAgentLabelShort},
}

/** How far each arrow key moves the selection along the segment list; 0 means "not our key". */
const keyStep = (key: string): number => {
  if (key === 'ArrowRight' || key === 'ArrowDown') return 1
  if (key === 'ArrowLeft' || key === 'ArrowUp') return -1
  return 0
}

type NetworkViewToggleProps = {
  /** The views the viewer is allowed to see, in display order. */
  views: PammNetworkViewId[]
  /** The view currently rendered below the toggle. */
  activeView: PammNetworkViewId
  /** Called with the view the user selected; never called with the already-active view. */
  onChange: (view: PammNetworkViewId) => void
} & WithNamespaces

/**
 * Segmented control that switches the tree between the Master and Agent perspectives (PT-5719).
 *
 * Implemented as a tab list rather than a pair of buttons because that is exactly what it is: two
 * labels selecting between two renderings of the same panel. That also gives the expected keyboard
 * model for free — a single tab stop into the control (roving `tabIndex`) and arrow keys to move
 * between segments, selecting as they go.
 *
 * @param views - Views available to the viewer; the control is only worth rendering for two or more.
 * @param activeView - The currently selected view.
 * @param onChange - Selection handler.
 * @param t - Translation function injected by `withNamespaces`, used for the group aria-label.
 */
const _NetworkViewToggle = ({views, activeView, onChange, t}: NetworkViewToggleProps) => {
  const segmentRefs = useRef<Partial<Record<PammNetworkViewId, HTMLButtonElement | null>>>({})

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    const step = keyStep(event.key)
    if (step === 0) return
    // Stop the arrow key from also scrolling the (scrollable) tree behind the control.
    event.preventDefault()
    const nextIndex = (views.indexOf(activeView) + step + views.length) % views.length
    const nextView = views[nextIndex]
    if (nextView === activeView) return
    onChange(nextView)
    // Focus follows selection, so a keyboard user's next arrow press continues from the new segment.
    segmentRefs.current[nextView]?.focus()
  }

  return (
    <Segmented
      role="tablist"
      aria-label={t(messages.pammViewToggleAria.i18nKey, messages.pammViewToggleAria.defaults)}
      data-testid="pamm-view-toggle"
    >
      {views.map((view) => {
        const isActive = view === activeView
        return (
          <Segment
            key={view}
            ref={(node: HTMLButtonElement | null) => {
              segmentRefs.current[view] = node
            }}
            type="button"
            role="tab"
            id={viewTabId(view)}
            aria-selected={isActive}
            aria-controls={NETWORK_PANEL_ID}
            // Roving tab index: the control is one stop in the page's tab order, not two.
            tabIndex={isActive ? 0 : -1}
            isActive={isActive}
            onClick={() => !isActive && onChange(view)}
            onKeyDown={handleKeyDown}
            data-testid={`pamm-view-${view}`}
          >
            <FullLabel>
              <Trans {...VIEW_LABELS[view].full} />
            </FullLabel>
            <ShortLabel>
              <Trans {...VIEW_LABELS[view].short} />
            </ShortLabel>
          </Segment>
        )
      })}
    </Segmented>
  )
}

export const NetworkViewToggle = withNamespaces()(_NetworkViewToggle)
